import { ComponentTierName, CompressorActivityStateEnum, ControlModeEnum, SensorsPackage } from '@/constants/enums';

import { useTranslation } from 'react-i18next';
import { camelCase, isNil, snakeCase } from 'lodash';
import numeral from 'numeral';
import moment from 'moment';

import UserStore from '../store/User/UserStore';
import StationStore from '../store/Station/StationStore';
import permissionsConfig from '../constants/permissionsConfig';
import { InsightEvent } from '@/store/InsightsStore';
import fr from 'antd/lib/locale/fr_FR';
import en from 'antd/lib/locale/en_US';
import es from 'antd/lib/locale/es_ES';
import pt from 'antd/lib/locale/pt_PT';

export const getFirstDeviceByIndex = (onDevicesArr, deviceType) =>
  [...onDevicesArr]
    .sort((d1, d2) => d1.elementIndex - d2.elementIndex)
    .find(device => device.deviceType === deviceType);

export const isValueInEnum = (value: string, enumObject: object): boolean => {
  return Object.values(enumObject).includes(camelCase(value));
};

export const toTranslationFormat = text => {
  /** Used to transform text to translation convention format. i.e. : nextServiceHours => NEXT_SERVICE_HOURS */
  return snakeCase(text).toUpperCase();
};

export const getControlModeValue = (controlMode, t) => {
  if (!isValueInEnum(controlMode, ControlModeEnum)) {
    return null;
  }

  return t(toTranslationFormat(controlMode));
};

export const getWorkingHoursValue = entity => {
  if (!entity.runMinutes) return null;
  const hours = Math.floor(entity.runMinutes / 60);
  const minutes = entity.runMinutes % 60;
  return `${hours < 10 ? `0${hours}` : hours}:${minutes < 10 ? `0${minutes}` : minutes}`;
};

export const useTranslateEnum = value => {
  const { t } = useTranslation();
  return t(toTranslationFormat(value));
};

export const formatEngineConfigDate = date => {
  return moment(date).format('MMMM DD, YYYY h:mm');
};

export const getActivityValue = (activity, t) => {
  if (!isValueInEnum(activity, CompressorActivityStateEnum)) return null;

  return t(toTranslationFormat(activity));
};

export const formatThousands = value => numeral(value).format('0,0[.]0');

export const onlyDigits = event =>
  !((event?.charCode >= 48 && event?.charCode <= 57) || event.charCode === 46) && event.preventDefault();

export const onlyDigitsAndDash = event =>
  !((event?.charCode >= 48 && event?.charCode <= 57) || event?.charCode === 45 || event?.charCode === 46) &&
  event.preventDefault();

export const maxLength = max => ({
  max,
  exceedFormatter: (value, { max }) =>
    value
      ? value
          .split('')
          .slice(0, value.includes('.') ? max + 2 : max)
          .join('')
      : value,
});

export const isEcoplantAdmin = (userStore: UserStore, stationStore: StationStore, stationId?: string): boolean => {
  return hasSpecificRole(userStore, stationStore, ['Ecoplant', 'Admin'], stationId);
};
export const isEcoplantShowcase = (userStore: UserStore, stationStore: StationStore, stationId?: string): boolean => {
  return hasSpecificRole(userStore, stationStore, 'Ecoplant Showcase', stationId);
};

export const hasSpecificRole = (
  userStore: UserStore,
  stationStore: StationStore,
  roleNames: string | string[],
  stationId?: string,
): boolean => {
  let currentStationId = stationStore?.currentStation?.id;
  if (stationId) {
    currentStationId = stationId;
  }
  // checking roleNames as an array to accomodate any future admin role changes in Retool
  if (Array.isArray(roleNames))
    return roleNames?.every(roleName =>
      userStore.user.stations.find(station => station.id === currentStationId)?.roleName.includes(roleName),
    );
  return userStore.user.stations.find(station => station.id === currentStationId)?.roleName === roleNames;
};
export const checkTierPermission = (componentName: ComponentTierName, stationStore: StationStore): boolean => {
  const currentTier = stationStore?.stationTier;
  const permission = permissionsConfig.find(perm => perm.component === componentName);
  return permission && !isNil(currentTier) ? permission.roles.includes(currentTier) : false;
};

export const isExposedEvent = (event: InsightEvent, stationStore: StationStore): boolean => {
  return ['system', stationStore.currentStation.name].includes(event.assetName);
};

export const checkInfoLockTierIsVisible = (
  componentName: ComponentTierName,
  type: SensorsPackage,
  stationStore: StationStore,
): boolean => {
  const isPermission = checkTierPermission(componentName, stationStore);
  return isPermission && !stationStore?.packageSensors?.includes(type);
};

export const waitFor = async (condFunc: () => boolean) => {
  return new Promise(resolve => {
    if (condFunc()) {
      resolve(null);
    } else {
      setTimeout(async () => {
        await waitFor(condFunc);
        resolve(null);
      }, 100);
    }
  });
};

export const matchesEveryQueryItem = (fields, query) => {
  if (!query) return true;
  const searchTerms = (query ?? '').split(' ');
  return (
    searchTerms.length === 0 || searchTerms.every((term, i) => fields[i]?.toLowerCase().includes(term.toLowerCase()))
  );
};

export const getFullStationName = station => {
  if (isNil(station)) return '';
  return `${station.customerName} | ${station.siteName} | ${station.name}`;
};

export const getLanguage = savedLanguage => {
  const supportedLanguages = { fr, en, es, pt };
  return supportedLanguages[savedLanguage] || en;
};

export const keyBy = <T>(array: T[], key: keyof T): Record<string, T> => {
  return array.reduce((acc, item) => {
    const keyValue = item[key] as string;
    acc[keyValue] = item;
    return acc;
  }, {});
};

export const isAndroid = () => /Android/i.test(navigator.userAgent);
